

import React, { useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { DollarSign, Users, Briefcase, Star, TrendingUp, Download } from 'lucide-react';
import '../../../../assets/css/screens/admin/dashboard/reports/ViewReports.css';

// ── Time filter tabs ──────────────────────────────────────────
const TIME_TABS = ['Last 7 days', 'Last 30 days', 'Last 6 months', 'All Time'];

// ── Stat cards ────────────────────────────────────────────────
const STATS = [
  { id: 'revenue', icon: <DollarSign size={18} />, label: 'Total Revenue',    value: '$2000', change: '+891', positive: true },
  { id: 'users',   icon: <Users      size={18} />, label: 'New Users (Month)', value: '342',   change: '+286', positive: true },
  { id: 'jobs',    icon: <Briefcase  size={18} />, label: 'Completed Jobs',    value: '70', change: '+101', positive: true },
  { id: 'rating',  icon: <Star       size={18} />, label: 'Avg. Rating',       value: '4.0',   change: '+0.2', positive: true },
];

// ── Revenue trend data ────────────────────────────────────────
const REVENUE_DATA = [
  { month: 'Oct', revenue: 820000  },
  { month: 'Nov', revenue: 932000  },
  { month: 'Dec', revenue: 901000  },
  { month: 'Jan', revenue: 1040000 },
  { month: 'Feb', revenue: 1250000 },
  { month: 'Mar', revenue: 1420000 },
];

// ── Category pie data ─────────────────────────────────────────
const CATEGORY_DATA = [
  { name: 'Electrical (30%)', value: 30, color: '#3b82f6' },
  { name: 'Plumbing (28%)',   value: 28, color: '#10b981' },
  { name: 'Cleaning (18%)',   value: 18, color: '#f59e0b' },
  { name: 'AC Repair (15%)', value: 15, color: '#0491e1' },
  { name: 'Others (9%)',     value: 9,  color: '#94a3b8' },
];

// ── User growth data ──────────────────────────────────────────
const USER_GROWTH = [
  { month: 'Oct', Customers: 1800, Providers: 620  },
  { month: 'Nov', Customers: 2200, Providers: 780  },
  { month: 'Dec', Customers: 2600, Providers: 900  },
  { month: 'Jan', Customers: 3000, Providers: 1050 },
  { month: 'Feb', Customers: 3400, Providers: 1200 },
  { month: 'Mar', Customers: 3800, Providers: 1380 },
];

// ── Monthly bookings data ─────────────────────────────────────
const MONTHLY_BOOKINGS = [
  { month: 'Oct', bookings: 145 },
  { month: 'Nov', bookings: 178 },
  { month: 'Dec', bookings: 160 },
  { month: 'Jan', bookings: 210 },
  { month: 'Feb', bookings: 260 },
  { month: 'Mar', bookings: 295 },
];

// ── Custom tooltip ────────────────────────────────────────────
const RevenueTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ar-tooltip">
      <p className="ar-tooltip-label">{label}</p>
      <p className="ar-tooltip-val">${(payload[0].value / 1000).toFixed(0)}K</p>
    </div>
  );
};

// ── Export to CSV ─────────────────────────────────────────────
const handleExport = () => {
  // Build CSV content from all data
  const sections = [
    ['Revenue & Bookings Trend'],
    ['Month', 'Revenue ($)'],
    ...REVENUE_DATA.map(r => [r.month, r.revenue]),
    [],
    ['Bookings by Category'],
    ['Category', 'Percentage (%)'],
    ...CATEGORY_DATA.map(c => [c.name, c.value]),
    [],
    ['User Growth'],
    ['Month', 'Customers', 'Providers'],
    ...USER_GROWTH.map(u => [u.month, u.Customers, u.Providers]),
    [],
    ['Monthly Bookings'],
    ['Month', 'Bookings'],
    ...MONTHLY_BOOKINGS.map(b => [b.month, b.bookings]),
  ];

  const csv = sections.map(row => row.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = `workhub-reports-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────────────────────
const ReportsAnalytics = () => {
  const [activeTab, setActiveTab] = useState('Last 6 months');

  return (
    <div className="ar-page">
      <div className="ar-container">

        {/* ════ PAGE HEADER ════ */}
        <div className="ar-page-header">
          <div className="ar-page-header-left">
            <h2 className="ar-heading">Reports &amp; Analytics</h2>
            <p className="ar-subtext">Platform performance overview</p>
          </div>
          <div className="ar-page-header-right">
            <div className="ar-time-tabs">
              {TIME_TABS.map(tab => (
                <button
                  key={tab}
                  className={`ar-time-tab${activeTab === tab ? ' ar-time-tab--active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
            <button className="ar-export-btn" onClick={handleExport}>
              <Download size={14} /> Export
            </button>
          </div>
        </div>

        {/* ════ STAT CARDS ════ */}
        <div className="ar-stats-grid">
          {STATS.map((stat, i) => (
            <div
              key={stat.id}
              className="ar-stat-card"
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="ar-stat-icon">{stat.icon}</div>
              <div className="ar-stat-body">
                <p className="ar-stat-value">{stat.value}</p>
                <p className="ar-stat-label">{stat.label}</p>
              </div>
              <span className={`ar-stat-change${stat.positive ? ' ar-stat-change--up' : ' ar-stat-change--down'}`}>
                <TrendingUp size={12} />
                {stat.change}
              </span>
            </div>
          ))}
        </div>

        {/* ════ REVENUE TREND ════ */}
        <div className="ar-chart-card" style={{ animationDelay: '0.32s' }}>
          <h5 className="ar-chart-title">Revenue &amp; Bookings Trend</h5>
          <div className="ar-chart-wrap ar-chart-wrap--tall">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={REVENUE_DATA}
                margin={{ top: 12, right: 20, left: 10, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#0491e1" stopOpacity={0.22} />
                    <stop offset="95%" stopColor="#0491e1" stopOpacity={0}    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#7a7874' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={v => `$${(v / 1000).toFixed(0)}K`}
                  tick={{ fill: '#7a7874' }}
                  axisLine={false}
                  tickLine={false}
                  width={58}
                  domain={[0, 'dataMax + 200000']}
                />
                <Tooltip content={<RevenueTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#0491e1"
                  strokeWidth={2.5}
                  fill="url(#revenueGrad)"
                  dot={{ fill: '#0491e1', r: 4, strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: '#0491e1' }}
                  isAnimationActive={true}
                  animationDuration={1200}
                  animationEasing="ease-out"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ════ PIE + USER GROWTH ════ */}
        <div className="ar-two-col">

          {/* Category pie */}
          <div className="ar-chart-card" style={{ animationDelay: '0.42s' }}>
            <h5 className="ar-chart-title">Bookings by Category</h5>
            <div className="ar-chart-wrap ar-chart-wrap--medium">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={CATEGORY_DATA}
                    cx="50%"
                    cy="50%"
                    innerRadius="48%"
                    outerRadius="72%"
                    paddingAngle={3}
                    dataKey="value"
                    isAnimationActive={true}
                    animationBegin={200}
                    animationDuration={1000}
                  >
                    {CATEGORY_DATA.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={v => `${v}%`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="ar-pie-legend">
              {CATEGORY_DATA.map((item, i) => (
                <span key={i} className="ar-legend-item">
                  <span className="ar-legend-dot" style={{ background: item.color }} />
                  {item.name}
                </span>
              ))}
            </div>
          </div>

          {/* User growth */}
          <div className="ar-chart-card" style={{ animationDelay: '0.50s' }}>
            <h5 className="ar-chart-title">User Growth</h5>
            <div className="ar-chart-wrap ar-chart-wrap--medium">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={USER_GROWTH}
                  barSize={14}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fill: '#7a7874' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#7a7874' }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Legend
                    wrapperStyle={{ paddingTop: '10px' }}
                    formatter={v => <span style={{ color: '#484745' }}>{v}</span>}
                  />
                  <Bar dataKey="Customers" fill="#0491e1" radius={[4,4,0,0]} isAnimationActive animationDuration={900} />
                  <Bar dataKey="Providers" fill="#10b981" radius={[4,4,0,0]} isAnimationActive animationDuration={900} animationBegin={200} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

        </div>

        {/* ════ MONTHLY BOOKINGS ════ */}
        <div className="ar-chart-card" style={{ animationDelay: '0.58s' }}>
          <h5 className="ar-chart-title">Monthly Bookings</h5>
          <div className="ar-chart-wrap ar-chart-wrap--medium">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={MONTHLY_BOOKINGS}
                barSize={36}
                margin={{ top: 8, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fill: '#7a7874' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#7a7874' }} axisLine={false} tickLine={false} />
                <Tooltip />
                <Bar dataKey="bookings" fill="#f59e0b" radius={[5,5,0,0]} isAnimationActive animationDuration={1000} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ReportsAnalytics;